import { ButtonHTMLAttributes, ComponentClass, createElement, forwardRef, FunctionComponent, NamedExoticComponent, ReactNode } from "react";
import classNames from "classnames";
import { RefType } from "../../../types";
import { genericMemo } from "../../../utils";
import { Badge, BadgeProps } from "../../Badge";

import styles from "./styles.module.scss";

export type TabHeaderSize = "sm" | "md";
export type TabHeaderType =
  "primary"
  | "gray"
  | "white"
  | "underline"
  | "underlineFilled"
  | "line";

export type ExpectedTabHeaderProps = {
  onClick?: (...v: any) => unknown
};

export type BaseTabHeaderProps<P extends ExpectedTabHeaderProps = ButtonHTMLAttributes<HTMLButtonElement>, R extends unknown = HTMLButtonElement> = {
  ref?: RefType<R>,
  errors?: string[];
  current?: boolean
  tabSize?: TabHeaderSize,
  tabType?: TabHeaderType;
  disabled?: boolean;
  children?: ReactNode,
  className?: string,
  component?: string | FunctionComponent<P> | ComponentClass<P, any> | NamedExoticComponent<P>,
  badgeProps?: BadgeProps,
}

export type TabHeaderProps<P extends ExpectedTabHeaderProps = ButtonHTMLAttributes<HTMLButtonElement>, R extends unknown = HTMLButtonElement> =
  BaseTabHeaderProps<P, R> & P;

const TabHeaderComponent = <P extends ExpectedTabHeaderProps = ButtonHTMLAttributes<HTMLButtonElement>, R extends unknown = HTMLButtonElement>(props: TabHeaderProps<P, R>) => <></>;
type TabHeaderComponentType = typeof TabHeaderComponent;

export const TabHeader = genericMemo(forwardRef(<P extends ExpectedTabHeaderProps = ButtonHTMLAttributes<HTMLButtonElement>, R extends unknown = HTMLButtonElement>(
  {
    component = "button",
    disabled = false,
    tabSize = "md",
    tabType = "primary",
    className,
    children,
    current = true,
    errors,
    badgeProps,
    ...props
  }: TabHeaderProps<P, R>,
  ref: RefType<R>,
) => {
  return createElement(component as any, {
    ref,
    disabled,
    className: classNames(
      className,
      styles.root,
      styles[tabSize as keyof typeof styles],
      styles[tabType as keyof typeof styles],
      {
        [styles.current]: current,
        [styles.disabled]: disabled,
      },
    ),
    children: <>
      {children}
      {badgeProps &&
        <Badge
          badgeType={"pillColor"}
          badgeSize={tabSize}
          {...badgeProps}
          className={classNames(badgeProps.className, styles.badge)}
        />
      }
      {Boolean(errors?.length) &&
        <Badge
          badgeType={"pillOutline"}
          badgeSize={tabSize}
          badgeColor={"error"}
          className={styles.badge}
        >
          {errors?.length}
        </Badge>
      }
    </>,
    ...props,
  });
}) as TabHeaderComponentType);
